import { ApiError } from '@clockwise/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { AlertTriangle, Share2, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { ConfirmDialog } from '@/components/custom/confirm-dialog';
import { NoAccess } from '@/components/custom/no-access';
import { shareReach } from '@/components/custom/share-badge';
import { PageHeader } from '@/components/layout/page-header';
import { PageShell } from '@/components/layout/page-shell';
import { decodeDrill, encodeDrill } from '@/components/reports/drill-chain';
import { ReportWorkbench } from '@/components/reports/report-workbench';
import { ShareReportDialog } from '@/components/reports/share-report-dialog';
import { DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { reportDefinitionsApi } from '@/lib/api';
import { useMe } from '@/lib/me';
import { queryKeys } from '@/lib/query-keys';

/**
 * One saved report — `/reports/custom/$reportId`.
 *
 * Opening it RUNS it. Nothing about the numbers is stored: the row carries a
 * spec, this page hands that spec to the analytics endpoint, and the endpoint
 * scopes the rows to whoever is signed in. So the same link sent to two people
 * shows each of them their own answer, and sending it discloses nothing.
 *
 * Reaching this page needs `report.view` alone — a recipient with no
 * `report.edit` can open, run and export what was shared with them (CR-1.5).
 * What they cannot do is save over it, share it or delete it; those controls
 * key off `report.owned`, which the server decides.
 */
export const Route = createFileRoute('/_authenticated/reports/custom/$reportId')({
  component: SavedReportPage,
  // Same `?sql=true` diagnostic as `/reports/new`, and the same reason it is a
  // param rather than a control. Optional in the parsed shape so a link to a
  // saved report does not have to carry it. Only the literal string counts:
  // `Boolean('false')` is `true`.
  // `?drill=` is the drill chain, so a drill is a link: the report, then each
  // click and where it landed. Decoded leniently — a mangled one is no drill,
  // not an error — see `decodeDrill`.
  validateSearch: (search: Record<string, unknown>): { sql?: true; drill?: string } => ({
    ...(search.sql === 'true' || search.sql === true ? { sql: true as const } : {}),
    ...(typeof search.drill === 'string' && search.drill ? { drill: search.drill } : {}),
  }),
});

function SavedReportPage() {
  const { reportId } = Route.useParams();
  const { sql: includeSql = false, drill: drillParam } = Route.useSearch();
  // By the STRING: decoding makes a new array, and a new chain every render
  // would recompute every landing under it.
  const chain = useMemo(() => decodeDrill(drillParam), [drillParam]);
  const { permissions, loading } = useMe();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [sharing, setSharing] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const report = useQuery({
    queryKey: queryKeys.reportDefinitions.detail(reportId),
    queryFn: () => reportDefinitionsApi.get(reportId).then((r) => r.data),
    // A report someone else edited should show its current definition, so this
    // is not cached indefinitely the way the model catalog is.
    staleTime: 30_000,
    retry: false,
  });

  const remove = useMutation({
    mutationFn: () => reportDefinitionsApi.remove(reportId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.reportDefinitions.all });
      toast.success('Report deleted');
      navigate({ to: '/reports' });
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Could not delete'),
  });

  const mayEdit = permissions.has('report.edit');
  const mayShare = permissions.has('report.share');

  if (!loading && !permissions.has('report.view')) {
    return <NoAccess permission="report.view" resource="reports" />;
  }

  // `loading` AND NOT JUST THE REPORT. `useMe` reports an EMPTY permission set
  // while its query is in flight, so `mayEdit` is provisionally false until it
  // lands — and this page's three requests race: `/api/users/me`, this report,
  // and the model catalog. Rendering on the first two alone mounted the
  // workbench with `mayEdit: false` whenever `me` came last, which drew the
  // builder with NO configuration column and no Save, then popped both in a
  // beat later. A guard on `mayEdit` itself would not have helped: false is
  // exactly what an empty set already answers. The page has to wait.
  //
  // `/reports/new` never had the bug because its permission gate withholds the
  // whole page on `loading`; this is that, in the shape this route needs.
  if (loading || report.isPending) {
    return (
      <PageShell>
        <Skeleton className="h-9 w-72" />
        <Skeleton className="h-96 w-full" />
      </PageShell>
    );
  }

  // G15: a report that is gone, or was never shared with this caller, is a
  // legible state and not a blank page. The server answers NOT_FOUND for both —
  // deliberately, since "there is a report here you may not open" is itself a
  // disclosure — so this message covers the two without claiming to tell them
  // apart.
  if (report.isError) {
    const missing = report.error instanceof ApiError && report.error.code === 'NOT_FOUND';
    return (
      <PageShell>
        <PageHeader title="Report" />
        <div
          role="alert"
          className="flex items-start gap-2 rounded-lg border border-destructive/50 bg-destructive/5 px-3 py-2 text-sm"
        >
          <AlertTriangle className="mt-0.5 size-4 shrink-0 text-destructive" aria-hidden />
          <p className="min-w-0">
            <span className="font-medium text-destructive">
              {missing ? 'This report isn’t available.' : 'Couldn’t load this report.'}
            </span>{' '}
            {missing
              ? 'It may have been deleted, or it isn’t shared with you.'
              : report.error instanceof ApiError
                ? report.error.message
                : 'Please try again.'}
          </p>
        </div>
      </PageShell>
    );
  }

  const saved = report.data;
  const canShare = saved.owned && mayShare;
  const canDelete = saved.owned && mayEdit;

  return (
    <PageShell>
      {/* The workbench draws the whole header — the report's name, its
          description, and every action on it. Share and Delete are handed to it
          as MENU ITEMS: they belong to a SAVED report and only exist on this
          route, which is why they are built here rather than inside a component
          `/reports/new` also uses. */}
      <ReportWorkbench
        report={saved}
        // Only ever seen by a report saved without a name, which the naming
        // dialog makes hard to do — the heading is the report's own name, read
        // off the draft so a rename shows where it was made.
        fallbackTitle="Untitled report"
        includeSql={includeSql}
        mayEdit={mayEdit}
        drill={{
          chain,
          // REPLACE, not push: each level is one state of one page, and Back
          // in the browser should leave the report rather than walk the tray.
          onChange: (next) =>
            navigate({
              to: '.',
              search: (prev) => ({ ...prev, drill: encodeDrill(next) }),
              replace: true,
            }),
        }}
        menuItems={
          // Owner-only, both of them. Sharing additionally needs
          // `report.share`: a recipient cannot pass on what they were given,
          // and someone who may build reports is not thereby someone who may
          // hand them around.
          canShare || canDelete ? (
            <>
              {canShare && (
                <DropdownMenuItem onSelect={() => setSharing(true)}>
                  <Share2 className="size-4" />
                  Share
                </DropdownMenuItem>
              )}
              {canDelete && (
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  disabled={remove.isPending}
                  onSelect={() => setConfirmingDelete(true)}
                >
                  <Trash2 className="size-4" />
                  Delete
                </DropdownMenuItem>
              )}
            </>
          ) : undefined
        }
      />

      {sharing && <ShareReportDialog report={saved} open={sharing} onOpenChange={setSharing} />}

      <ConfirmDialog
        open={confirmingDelete}
        title={`Delete “${saved.name}”?`}
        description={
          saved.shares.length > 0
            ? `This report is shared with ${shareReach(saved.shares)}. Deleting it removes it from their list too, and can’t be undone.`
            : 'This can’t be undone.'
        }
        confirmLabel="Delete"
        destructive
        confirmDisabled={remove.isPending}
        onConfirm={() => {
          setConfirmingDelete(false);
          remove.mutate();
        }}
        onCancel={() => setConfirmingDelete(false)}
      />
    </PageShell>
  );
}
